/**
 * Recording clickwrap acceptances (Terms, Privacy, Expert Agreement,
 * recording consent) against POST /api/legal/acceptances.
 *
 * Why this is its own module: the previous inline version read the auth token
 * from the browser-storage key named "token", which nothing writes (the app
 * stores "xpert_token"), so it threw on every call, and both sign-up pages swallowed
 * the throw with console.warn. `legal_acceptances` stayed empty in prod. This
 * module takes the token from authStorage, retries what a retry can fix, and
 * throws a typed error that callers must handle. It has no React or webpack
 * dependency so it can be unit-tested; the dynamic `import()` of the policy
 * text stays in components/LegalAcceptanceCheckbox.jsx and is injected here.
 */
import { getUserToken } from "./authStorage";

// Must equal the `version` registered in legal_audit.policy_versions
// (migration 048). lib/legalPolicyContract.test.js checks it.
export const POLICY_VERSION = "1.1.0";

const POLICY_LABELS = {
  terms_of_service: "Terms of Service",
  privacy_policy: "Privacy Policy",
  expert_agreement: "Expert Agreement",
  recording_consent: "Recording & AI Consent",
};

export const SUPPORT_EMAIL = "legal@xpert.company";

export class LegalAcceptanceError extends Error {
  /**
   * @param {string} message  for logs; not shown to users (see describeAcceptanceFailure)
   * @param {{code?: "no_token"|"failed", failures?: Array<{policyId: string, status: number|null, detail: string}>, recorded?: string[]}} info
   *   `recorded` lists the policies that DID land, so a retry can skip them.
   */
  constructor(message, { code = "failed", failures = [], recorded = [] } = {}) {
    super(message);
    this.name = "LegalAcceptanceError";
    this.code = code;
    this.failures = failures;
    this.recorded = recorded;
  }
}

export async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Network failure (0), rate limit, or server error: a retry can plausibly fix it. */
export function isRetryable(status) {
  return status === 0 || status === 429 || status >= 500;
}

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function detailOf(res) {
  try {
    const raw = await res.text();
    try {
      const parsed = JSON.parse(raw);
      const d = parsed && parsed.detail;
      return String(typeof d === "string" ? d : JSON.stringify(d ?? raw)).slice(0, 200);
    } catch {
      return String(raw).slice(0, 200);
    }
  } catch {
    return "";
  }
}

async function postWithRetry({ url, token, payload, fetchImpl, retries, backoffMs, sleep }) {
  let last = { ok: false, status: 0, detail: "" };
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    if (attempt > 0) await sleep(backoffMs * 2 ** (attempt - 1));
    try {
      const res = await fetchImpl(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) return { ok: true };
      last = { ok: false, status: res.status, detail: await detailOf(res) };
    } catch (e) {
      last = { ok: false, status: 0, detail: (e && e.message) || "network error" };
    }
    if (!isRetryable(last.status)) break;
  }
  return last;
}

/**
 * Record an acceptance for each policy id. Resolves `{ recorded }` only if
 * every policy is on file; otherwise throws LegalAcceptanceError.
 *
 * Pass `alreadyRecorded` (from a previous error's `.recorded`) to skip policies
 * that already landed, so a user-initiated retry never writes a duplicate row.
 */
export async function recordAcceptances({
  apiBase,
  policyIds,
  version = POLICY_VERSION,
  token,
  bookingId,
  alreadyRecorded = [],
  loadPolicyText,
  hash = sha256Hex,
  fetchImpl = (...args) => fetch(...args),
  retries = 2,
  backoffMs = 500,
  sleep = defaultSleep,
}) {
  const tok = token || getUserToken();
  if (!tok) {
    throw new LegalAcceptanceError("not signed in: no auth token to record acceptances with", {
      code: "no_token",
      recorded: [...alreadyRecorded],
    });
  }

  const done = new Set(alreadyRecorded);
  const failures = [];
  for (const policyId of policyIds) {
    if (done.has(policyId)) continue;
    let textHash;
    try {
      textHash = await hash(await loadPolicyText(policyId));
    } catch (e) {
      failures.push({ policyId, status: null, detail: (e && e.message) || "could not load policy text" });
      continue;
    }
    const payload = {
      policy_id: policyId,
      policy_version: version,
      text_hash_at_acceptance: textHash,
    };
    if (bookingId) payload.booking_id = bookingId;

    const result = await postWithRetry({
      url: `${apiBase}/legal/acceptances`,
      token: tok,
      payload,
      fetchImpl,
      retries,
      backoffMs,
      sleep,
    });
    if (result.ok) done.add(policyId);
    else failures.push({ policyId, status: result.status, detail: result.detail });
  }

  const recorded = policyIds.filter((id) => done.has(id));
  if (failures.length > 0) {
    throw new LegalAcceptanceError(
      `Failed to record ${failures.length} of ${policyIds.length} acceptances: ` +
        failures.map((f) => `${f.policyId}: ${f.status ?? "-"} ${f.detail}`).join("; "),
      { code: "failed", failures, recorded },
    );
  }
  return { recorded };
}

/** User-facing copy. Deliberately never includes a server body or stack. */
export function describeAcceptanceFailure(err) {
  if (err && err.code === "no_token") {
    return "We couldn't confirm you're signed in, so your acceptance wasn't recorded. Please sign in again and retry.";
  }
  const ids = ((err && err.failures) || []).map((f) => f.policyId);
  const names = ids.map((id) => POLICY_LABELS[id] || id);
  const what = names.length > 0 ? names.join(", ") : "the agreements";
  return (
    `We couldn't record your acceptance of: ${what}. This is usually a brief network or server problem and nothing you entered is lost. ` +
    `Please try again. If it keeps failing, email ${SUPPORT_EMAIL}.`
  );
}
